import { useEffect, useRef } from 'react'

import {
  GreetingConditionTypeEnum,
  getGreetingConditionTypeString,
  greetingByIdQuery,
  greetingConditionTypeArray,
} from '@/entities/greetings'
import { confirm } from '@/shared/lib/antd-utils'
import {
  hasFormChanged,
  parseDateStringToDayjsDate,
} from '@/shared/lib/form-utils'
import { showNotificationWarningFx } from '@/shared/lib/providers'
import {
  HidingSpin,
  SelfValidateInput,
  SelfValidateTextArea,
} from '@/shared/ui'
import { PrimaryButton } from '@/shared/ui'
import type { SelfValidateInputRef, SelfValidateTextAreaRef } from '@/shared/ui'
import { Button, DatePicker, Flex, Form, Modal, Radio } from 'antd'
import { type Dayjs } from 'dayjs'
import { useGate, useUnit } from 'effector-react'
import { useFormik } from 'formik'
import { z } from 'zod'
import { toFormikValidationSchema } from 'zod-formik-adapter'

import { getGreetingSubmitRequestByFormValues } from './lib/get-greeting-submit-request'
import {
  $isGreetingFormModalOpened,
  $submittingGreeting,
  GreetingFormGate,
  greetingFormModalClosed,
  greetingSubmitted,
} from './model/greeting-form.model'
import InsertUsernameTagButton from './ui/InsertUsernameButton'

export { greetingFormModalOpened } from './model/greeting-form.model'

const greetingSchema = z.object({
  name: z.string(),
  title: z.string(),
  description: z.string(),
  conditionType: z.custom<GreetingConditionTypeEnum>(),
  sendOnStartDate: z.custom<Dayjs>().nullable().optional(),
  sendOnEndDate: z.custom<Dayjs>().nullable().optional(),
})

export type GreetingFormModel = z.infer<typeof greetingSchema>

interface GreetingFormProps {
  isModal?: boolean
}
export const GreetingForm = ({ isModal }: GreetingFormProps) => {
  //   Queries
  const {
    data: greetingData,
    pending: greetingPending,
    error: greetingError,
  } = useUnit(greetingByIdQuery)
  const greetingQueryStatus = useUnit(greetingByIdQuery.$status)

  const submitGreeting = useUnit(greetingSubmitted)
  const submitting = useUnit($submittingGreeting)
  //   Modal
  const isModalOpened = useUnit($isGreetingFormModalOpened)
  const closeFormModal = useUnit(greetingFormModalClosed)
  //   Form state
  const formik = useFormik<GreetingFormModel>({
    enableReinitialize: true,
    initialValues: greetingData
      ? {
          name: greetingData.name,
          title: greetingData.title,
          description: greetingData.description,
          conditionType: greetingData.conditionType,
          sendOnStartDate: greetingData.sendOnStartDate
            ? parseDateStringToDayjsDate(greetingData.sendOnStartDate)
            : null,

          sendOnEndDate: greetingData.sendOnEndDate
            ? parseDateStringToDayjsDate(greetingData.sendOnEndDate)
            : null,
        }
      : {
          name: '',
          title: '',
          description: '',
          conditionType: greetingConditionTypeArray[0],
          sendOnStartDate: undefined,
          sendOnEndDate: undefined,
        },
    validationSchema: toFormikValidationSchema(greetingSchema),
    onSubmit: (values: GreetingFormModel, { setSubmitting }) => {
      if (
        (values.conditionType === GreetingConditionTypeEnum.OnDate &&
          !values.sendOnStartDate) ||
        (values.conditionType === GreetingConditionTypeEnum.OnPeriod &&
          (!values.sendOnStartDate || !values.sendOnEndDate))
      ) {
        {
          void showNotificationWarningFx({
            message: 'Не все поля заполнены',
            description: 'Дата начала и конца отправки должны быть заполнены',
          })
          setSubmitting(false)
          return
        }
      }
      const greetingSubmittedData = getGreetingSubmitRequestByFormValues(values)
      submitGreeting(greetingSubmittedData)
      setSubmitting(false)
    },
  })

  useGate(GreetingFormGate, {
    isModal,
    resetForm: formik.resetForm,
  })

  const { isValid, isValidating, isSubmitting } = formik

  const isDirty = hasFormChanged(formik.values, formik.initialValues)

  useEffect(() => {
    if (!isValid && !isValidating && isSubmitting) {
      void showNotificationWarningFx({ message: 'Не все поля заполнены' })
    }
  }, [isSubmitting, isValid, isValidating])

  // Prevent data lose when try to close

  useEffect(() => {
    if (isDirty) {
      const handler = (e: BeforeUnloadEvent) => {
        e.preventDefault()
      }
      window.addEventListener('beforeunload', handler)
      return () => {
        window.removeEventListener('beforeunload', handler)
      }
    }
  }, [isDirty])
  const tryToClose = async () => {
    if (isDirty) {
      const confirmed = await confirm({
        title: 'Данные не будут сохранены',
        content: 'Вы уверены, что хотите закрыть форму?',
        okText: 'Да, закрыть',
        cancelText: 'Отмена',
        centered: true,
      })
      if (!confirmed) return
    }
    formik.resetForm()
    closeFormModal()
  }

  const formControlButtonsDisabled =
    !hasFormChanged(formik.values, formik.initialValues) || submitting

  // Username Tag

  const inputRef = useRef<SelfValidateInputRef>(null)
  const textAreaRef = useRef<SelfValidateTextAreaRef>(null)

  const formComponent = (
    <HidingSpin
      spinning={
        greetingQueryStatus !== 'initial' &&
        (greetingPending || (!greetingData && !greetingError))
      }
    >
      <Form.Item layout="vertical" label="Название шаблона" required>
        <SelfValidateInput
          name="name"
          showCount
          maxLength={50}
          value={formik.values.name}
          onChange={(value) => {
            void formik.setFieldValue('name', value)
          }}
        />
      </Form.Item>

      <Form.Item layout="vertical" label="Заголовок" required>
        <Flex vertical gap={8}>
          <SelfValidateInput
            ref={inputRef}
            name="title"
            showCount
            maxLength={50}
            value={formik.values.title}
            onChange={(value) => {
              void formik.setFieldValue('title', value)
            }}
          />

          <InsertUsernameTagButton
            inputRef={inputRef}
            fieldName="title"
            formik={formik}
          />
        </Flex>
      </Form.Item>

      <Form.Item layout="vertical" label="Описание" required>
        <Flex vertical gap={8}>
          <SelfValidateTextArea
            ref={textAreaRef}
            name="description"
            showCount
            maxLength={200}
            value={formik.values.description}
            onChange={(value) => {
              void formik.setFieldValue('description', value)
            }}
          />
          <InsertUsernameTagButton
            inputRef={textAreaRef}
            fieldName="description"
            formik={formik}
          />
        </Flex>
      </Form.Item>
      <Form.Item label="Условия показа" layout="vertical" required>
        <Radio.Group
          optionType="button"
          name="conditionType"
          value={formik.values.conditionType}
          options={greetingConditionTypeArray.map((greetingConditionType) => ({
            value: greetingConditionType,
            label: getGreetingConditionTypeString(greetingConditionType),
          }))}
          onChange={(value) => {
            const newConditionType: GreetingConditionTypeEnum =
              value.target.value
            void formik.setFieldValue('conditionType', newConditionType)
          }}
        />
      </Form.Item>

      {formik.values.conditionType !== GreetingConditionTypeEnum.EveryDay && (
        <Form.Item
          layout="vertical"
          required
          label={
            formik.values.conditionType === GreetingConditionTypeEnum.OnDate
              ? 'Дата'
              : 'Период'
          }
          style={{ width: 'fit-content' }}
        >
          <Flex vertical gap={8}>
            {formik.values.conditionType ===
              GreetingConditionTypeEnum.OnPeriod && (
              <DatePicker.RangePicker
                format="DD-MM"
                inputReadOnly
                value={[
                  formik.values.sendOnStartDate,
                  formik.values.sendOnEndDate,
                ]}
                onChange={(value) => {
                  if (value && value.length === 2) {
                    if (value[0])
                      void formik.setFieldValue('sendOnStartDate', value[0])
                    if (value[1])
                      void formik.setFieldValue('sendOnEndDate', value[1])
                  }
                }}
              />
            )}
            {formik.values.conditionType ===
              GreetingConditionTypeEnum.OnDate && (
              <DatePicker
                format="DD-MM"
                inputReadOnly
                value={formik.values.sendOnStartDate}
                onChange={(value) => {
                  void formik.setFieldValue('sendOnStartDate', value)
                }}
              />
            )}
          </Flex>
        </Form.Item>
      )}

      <Flex justify="flex-end" gap={10}>
        {!greetingData && (
          <Button
            disabled={formControlButtonsDisabled}
            onClick={() => formik.resetForm()}
          >
            Очистить
          </Button>
        )}
        <PrimaryButton
          disabled={formControlButtonsDisabled}
          onClick={() => {
            formik.handleSubmit()
          }}
          loading={submitting}
        >
          {greetingData ? 'Сохранить' : 'Создать'}
        </PrimaryButton>
      </Flex>
    </HidingSpin>
  )

  return (
    <>
      {isModal ? (
        <Modal
          open={isModalOpened}
          onCancel={() => {
            void tryToClose()
          }}
          destroyOnHidden
          centered
          title={
            greetingQueryStatus === 'initial'
              ? 'Создание шаблона приветственного экрана'
              : greetingPending || (!greetingData && !greetingError)
                ? 'Загрузка'
                : 'Редактирование шаблона приветственного экрана'
          }
          footer={null}
        >
          {formComponent}
        </Modal>
      ) : (
        formComponent
      )}
    </>
  )
}
